import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { copyToClipboard as copyText } from '../lib/utils';
import { 
  Code, 
  Copy, 
  CheckCircle2, 
  Key, 
  Zap, 
  ArrowRight,
  ExternalLink,
  Terminal,
  FileJson,
  Shield,
  BookOpen
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Always use pravik.ai for documentation examples (production domain)
const DOCS_BASE_URL = 'https://pravik.ai';

export default function APIPage() {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(null);
  const [assets, setAssets] = useState([]);
  const [token, setToken] = useState('');

  useEffect(() => {
    fetchAssets();
    // Get token from localStorage for display - use the correct key
    const storedToken = localStorage.getItem('convee_token');
    if (storedToken) {
      setToken(storedToken);
    }
  }, []);

  const fetchAssets = async () => {
    try {
      const response = await axios.get(`${API}/assets`);
      setAssets(response.data);
    } catch (error) {
      console.error('Failed to fetch assets:', error);
    }
  };

  const handleCopy = async (text, id) => {
    const success = await copyText(text);
    if (success) {
      setCopied(id);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(null), 2000);
    } else {
      toast.error('Could not copy. Please select and copy manually.');
    }
  };

  const exampleAssetId = assets.length > 0 ? assets[0].id : 'your_asset_id';
  const exampleAssetSlug = assets.length > 0 && assets[0].asset_slug ? assets[0].asset_slug : 'rally-azure-spark';

  const exampleRequest = `curl -X POST "${DOCS_BASE_URL}/api/v1/campaigns" \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "asset_id": "${exampleAssetId}",
    "campaign_context": "Targeting startup founders in San Francisco looking for tax optimization services",
    "name": "SF Founders Campaign",
    "tags": ["q1-2026", "founders"]
  }'`;

  const exampleResponse = `{
  "campaign_id": "cmp_abc123",
  "magic_link": "${DOCS_BASE_URL}/api/proxy/cmp_abc123",
  "status": "success",
  "campaign_name": "SF Founders Campaign",
  "asset_id": "${exampleAssetId}",
  "goals_selected": 3,
  "sections_applied": 2,
  "variant_applied": "impact",
  "created_at": "2026-02-09T12:00:00Z"
}`;

  // Fast Campaign API examples
  const fastCampaignRequest = `curl -X POST "${DOCS_BASE_URL}/api/v1/fast-campaigns" \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "asset_id": "${exampleAssetId}",
    "context_text": "Boston users looking for tax optimization",
    "name": "Boston Landing Page"
  }'`;

  const fastCampaignResponse = `{
  "campaign_id": "fc_xyz789",
  "magic_link": "${DOCS_BASE_URL}/api/proxy/fc_xyz789",
  "status": "success",
  "campaign_name": "Boston Landing Page",
  "asset_id": "${exampleAssetId}",
  "fields_changed": 2,
  "diffs": [
    {
      "field": "Hero Headline",
      "original": "Your Taxes, Optimized Year-Round",
      "new": "Boston, Your Taxes Optimized Year-Round",
      "changed": true
    },
    {
      "field": "Hero Paragraph",
      "original": "Full-service tax filing...",
      "new": "Full-service tax filing for Boston residents...",
      "changed": true
    }
  ],
  "created_at": "2026-02-10T12:00:00Z"
}`;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-surface/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/dashboard')} className="text-muted-foreground hover:text-foreground transition-colors">
              ← Back
            </button>
            <h1 className="text-xl font-semibold text-foreground flex items-center gap-2">
              <Code className="w-5 h-5 text-primary" />
              API Documentation
            </h1>
          </div>
          <span className="px-2.5 py-1 text-xs font-medium bg-primary/10 text-primary rounded-full">
            v1
          </span>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-primary/5 to-transparent border border-border rounded-2xl p-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Zap className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-2">API Campaigns</h2>
              <p className="text-muted-foreground max-w-2xl">
                Create personalized marketing campaigns programmatically using the same AI-powered pipeline 
                as manual campaigns. Perfect for automation, integrations, and bulk campaign creation.
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-surface/50 rounded-xl p-4 border border-border/50">
              <Shield className="w-5 h-5 text-green-500 mb-2" />
              <h3 className="font-medium text-foreground mb-1">Full Parity</h3>
              <p className="text-sm text-muted-foreground">Same AI analysis, goals, and messaging as UI campaigns</p>
            </div>
            <div className="bg-surface/50 rounded-xl p-4 border border-border/50">
              <Zap className="w-5 h-5 text-yellow-500 mb-2" />
              <h3 className="font-medium text-foreground mb-1">Instant Magic Links</h3>
              <p className="text-sm text-muted-foreground">Get a shareable link immediately upon creation</p>
            </div>
            <div className="bg-surface/50 rounded-xl p-4 border border-border/50">
              <BookOpen className="w-5 h-5 text-blue-500 mb-2" />
              <h3 className="font-medium text-foreground mb-1">UI Editable</h3>
              <p className="text-sm text-muted-foreground">Review and modify API campaigns in the dashboard</p>
            </div>
          </div>
        </div>

        {/* Authentication Section */}
        <div className="bg-surface border border-border rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <Key className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-semibold text-foreground">Authentication</h3>
          </div>
          <p className="text-muted-foreground mb-4">
            All API requests require a Bearer token in the Authorization header. 
            Use the same token you receive from the login endpoint.
          </p>
          
          {/* Token Display Box */}
          <div className="bg-muted/30 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-muted-foreground">Your API Token:</span>
              <button 
                onClick={() => handleCopy(localStorage.getItem('convee_token') || '', 'token')}
                className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-primary/10 text-primary rounded-md hover:bg-primary/20 transition-colors"
              >
                {copied === 'token' ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                Copy Token
              </button>
            </div>
            
            {token ? (
              <div className="bg-gray-900 rounded-lg p-3 overflow-x-auto">
                <code className="text-xs text-green-400 break-all">{token}</code>
              </div>
            ) : (
              <div className="text-sm text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3">
                ⚠️ No token found. Please log in to see your API token.
              </div>
            )}
            
            <div className="flex items-center gap-2 pt-2 border-t border-border/50">
              <span className="text-xs text-muted-foreground">Header format:</span>
              <code className="text-xs font-mono text-primary bg-primary/10 px-2 py-0.5 rounded">Authorization: Bearer {token ? '••••••' : 'YOUR_TOKEN'}</code>
              <button 
                onClick={() => handleCopy(`Authorization: Bearer ${localStorage.getItem('convee_token') || 'YOUR_TOKEN'}`, 'auth')}
                className="p-1 rounded hover:bg-muted transition-colors"
              >
                {copied === 'auth' ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5 text-muted-foreground" />}
              </button>
            </div>
          </div>
        </div>

        {/* Endpoint Documentation */}
        <div className="bg-surface border border-border rounded-2xl overflow-hidden">
          <div className="bg-muted/30 px-6 py-4 border-b border-border flex items-center gap-3">
            <span className="px-2 py-1 text-xs font-bold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded">
              POST
            </span>
            <code className="font-mono text-foreground">/api/v1/campaigns</code>
          </div>
          
          <div className="p-6 space-y-6">
            <div>
              <h4 className="font-medium text-foreground mb-2">Description</h4>
              <p className="text-muted-foreground">
                Create a new campaign programmatically. This endpoint runs the full Auto-mode pipeline:
                goal inference, section strategy, messaging variants, and activation.
              </p>
            </div>

            {/* Request Body */}
            <div>
              <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
                <FileJson className="w-4 h-4" />
                Request Body
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 px-3 font-medium text-foreground">Field</th>
                      <th className="text-left py-2 px-3 font-medium text-foreground">Type</th>
                      <th className="text-left py-2 px-3 font-medium text-foreground">Required</th>
                      <th className="text-left py-2 px-3 font-medium text-foreground">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    <tr>
                      <td className="py-2 px-3 font-mono text-primary">asset_id</td>
                      <td className="py-2 px-3 text-muted-foreground">string</td>
                      <td className="py-2 px-3"><span className="text-red-500">Yes</span></td>
                      <td className="py-2 px-3 text-muted-foreground">The ID of the asset (website) to create campaign for</td>
                    </tr>
                    <tr>
                      <td className="py-2 px-3 font-mono text-primary">campaign_context</td>
                      <td className="py-2 px-3 text-muted-foreground">string</td>
                      <td className="py-2 px-3"><span className="text-red-500">Yes</span></td>
                      <td className="py-2 px-3 text-muted-foreground">Context describing the campaign, target audience, and goals</td>
                    </tr>
                    <tr>
                      <td className="py-2 px-3 font-mono text-primary">name</td>
                      <td className="py-2 px-3 text-muted-foreground">string</td>
                      <td className="py-2 px-3"><span className="text-muted-foreground">No</span></td>
                      <td className="py-2 px-3 text-muted-foreground">Campaign name (auto-generated if not provided)</td>
                    </tr>
                    <tr>
                      <td className="py-2 px-3 font-mono text-primary">external_id</td>
                      <td className="py-2 px-3 text-muted-foreground">string</td>
                      <td className="py-2 px-3"><span className="text-muted-foreground">No</span></td>
                      <td className="py-2 px-3 text-muted-foreground">Your external system reference ID</td>
                    </tr>
                    <tr>
                      <td className="py-2 px-3 font-mono text-primary">tags</td>
                      <td className="py-2 px-3 text-muted-foreground">string[]</td>
                      <td className="py-2 px-3"><span className="text-muted-foreground">No</span></td>
                      <td className="py-2 px-3 text-muted-foreground">Tags for organization and filtering</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Example Request */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-foreground flex items-center gap-2">
                  <Terminal className="w-4 h-4" />
                  Example Request
                </h4>
                <button 
                  onClick={() => handleCopy(exampleRequest, 'request')}
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {copied === 'request' ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                  Copy
                </button>
              </div>
              <pre className="bg-gray-900 text-gray-100 rounded-xl p-4 overflow-x-auto text-sm">
                <code>{exampleRequest}</code>
              </pre>
            </div>

            {/* Example Response */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-foreground flex items-center gap-2">
                  <FileJson className="w-4 h-4" />
                  Example Response (Success)
                </h4>
                <button 
                  onClick={() => handleCopy(exampleResponse, 'response')}
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {copied === 'response' ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                  Copy
                </button>
              </div>
              <pre className="bg-gray-900 text-gray-100 rounded-xl p-4 overflow-x-auto text-sm">
                <code>{exampleResponse}</code>
              </pre>
            </div>

            {/* Error Response */}
            <div>
              <h4 className="font-medium text-foreground mb-3">Error Responses</h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <span className="font-mono text-red-600 dark:text-red-400">404</span>
                  <span className="text-muted-foreground">Asset not found or access denied</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <span className="font-mono text-red-600 dark:text-red-400">400</span>
                  <span className="text-muted-foreground">Asset not ready (scraping not complete)</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <span className="font-mono text-red-600 dark:text-red-400">500</span>
                  <span className="text-muted-foreground">Campaign creation failed (pipeline error)</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Magic Link Section */}
        <div className="bg-surface border border-border rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <ExternalLink className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-semibold text-foreground">Magic Link Behavior</h3>
          </div>
          <div className="space-y-4 text-muted-foreground">
            <p>
              The <code className="text-primary bg-primary/10 px-1.5 py-0.5 rounded">magic_link</code> returned 
              in the response is the primary output of the API. This link:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Points directly to the personalized version of your website</li>
              <li>Automatically applies all AI-generated messaging changes</li>
              <li>Is safe to share externally with visitors</li>
              <li>Does not expose internal campaign IDs in the visible URL</li>
              <li>Remains stable - the same link always works for the campaign</li>
            </ul>
            <div className="bg-muted/30 rounded-xl p-4 mt-4">
              <p className="text-sm font-medium text-foreground mb-2">Example Magic Link:</p>
              <code className="text-primary">{DOCS_BASE_URL}/api/proxy/cmp_abc123</code>
            </div>
          </div>
        </div>

        {/* Fast Campaign API Section */}
        <div className="bg-gradient-to-br from-amber-500/5 to-transparent border border-amber-500/20 rounded-2xl p-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
              <Zap className="w-6 h-6 text-amber-500" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-2">Fast Campaigns API</h2>
              <p className="text-muted-foreground max-w-2xl">
                Create quick hero personalization campaigns with a single API call. Fast campaigns are ideal for 
                geo-targeting, demographic personalization, and rapid A/B testing.
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-surface/50 rounded-xl p-4 border border-border/50">
              <Zap className="w-5 h-5 text-amber-500 mb-2" />
              <h3 className="font-medium text-foreground mb-1">Instant Results</h3>
              <p className="text-sm text-muted-foreground">Single LLM call - get magic link in seconds</p>
            </div>
            <div className="bg-surface/50 rounded-xl p-4 border border-border/50">
              <Shield className="w-5 h-5 text-green-500 mb-2" />
              <h3 className="font-medium text-foreground mb-1">Conservative Changes</h3>
              <p className="text-sm text-muted-foreground">Only hero section - no risky full-page rewrites</p>
            </div>
            <div className="bg-surface/50 rounded-xl p-4 border border-border/50">
              <FileJson className="w-5 h-5 text-blue-500 mb-2" />
              <h3 className="font-medium text-foreground mb-1">Diff Output</h3>
              <p className="text-sm text-muted-foreground">See exactly what changed with before/after</p>
            </div>
          </div>
        </div>

        {/* Fast Campaign Endpoint */}
        <div className="bg-surface border border-amber-500/20 rounded-2xl overflow-hidden">
          <div className="bg-amber-500/10 px-6 py-4 border-b border-amber-500/20 flex items-center gap-3">
            <span className="px-2 py-1 text-xs font-bold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded">
              POST
            </span>
            <code className="font-mono text-foreground">/api/v1/fast-campaigns</code>
            <span className="ml-auto px-2 py-0.5 text-xs bg-amber-500/20 text-amber-600 dark:text-amber-400 rounded">Fast</span>
          </div>
          
          <div className="p-6 space-y-6">
            <div>
              <h4 className="font-medium text-foreground mb-2">Description</h4>
              <p className="text-muted-foreground">
                Create a fast campaign that personalizes the hero section based on your context. 
                Unlike full campaigns, this uses a single LLM call and only modifies the headline and hero paragraph.
              </p>
            </div>

            {/* Request Body */}
            <div>
              <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
                <FileJson className="w-4 h-4" />
                Request Body
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 px-3 font-medium text-foreground">Field</th>
                      <th className="text-left py-2 px-3 font-medium text-foreground">Type</th>
                      <th className="text-left py-2 px-3 font-medium text-foreground">Required</th>
                      <th className="text-left py-2 px-3 font-medium text-foreground">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    <tr>
                      <td className="py-2 px-3 font-mono text-amber-600 dark:text-amber-400">asset_id</td>
                      <td className="py-2 px-3 text-muted-foreground">string</td>
                      <td className="py-2 px-3"><span className="text-red-500">Yes</span></td>
                      <td className="py-2 px-3 text-muted-foreground">The ID of the asset (website) to personalize</td>
                    </tr>
                    <tr>
                      <td className="py-2 px-3 font-mono text-amber-600 dark:text-amber-400">context_text</td>
                      <td className="py-2 px-3 text-muted-foreground">string</td>
                      <td className="py-2 px-3"><span className="text-red-500">Yes</span></td>
                      <td className="py-2 px-3 text-muted-foreground">Personalization context (e.g., "Boston users", "startup founders")</td>
                    </tr>
                    <tr>
                      <td className="py-2 px-3 font-mono text-amber-600 dark:text-amber-400">name</td>
                      <td className="py-2 px-3 text-muted-foreground">string</td>
                      <td className="py-2 px-3"><span className="text-muted-foreground">No</span></td>
                      <td className="py-2 px-3 text-muted-foreground">Campaign name (auto-generated if not provided)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Example Request */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-foreground flex items-center gap-2">
                  <Terminal className="w-4 h-4" />
                  Example Request
                </h4>
                <button 
                  onClick={() => handleCopy(fastCampaignRequest, 'fast-request')}
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {copied === 'fast-request' ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                  Copy
                </button>
              </div>
              <pre className="bg-gray-900 text-gray-100 rounded-xl p-4 overflow-x-auto text-sm">
                <code>{fastCampaignRequest}</code>
              </pre>
            </div>

            {/* Example Response */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-foreground flex items-center gap-2">
                  <FileJson className="w-4 h-4" />
                  Example Response (Success)
                </h4>
                <button 
                  onClick={() => handleCopy(fastCampaignResponse, 'fast-response')}
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {copied === 'fast-response' ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                  Copy
                </button>
              </div>
              <pre className="bg-gray-900 text-gray-100 rounded-xl p-4 overflow-x-auto text-sm">
                <code>{fastCampaignResponse}</code>
              </pre>
            </div>

            {/* When to use Fast vs Full */}
            <div className="bg-muted/30 rounded-xl p-4">
              <h4 className="font-medium text-foreground mb-3">When to use Fast Campaigns vs Full Campaigns?</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="font-medium text-amber-600 dark:text-amber-400 mb-2">⚡ Fast Campaigns</p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-1">
                    <li>Geo-targeted landing pages</li>
                    <li>Quick A/B headline tests</li>
                    <li>Demographic personalization</li>
                    <li>Time-sensitive campaigns</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-primary mb-2">🎯 Full Campaigns</p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-1">
                    <li>Deep multi-section changes</li>
                    <li>Complex goal-based optimization</li>
                    <li>CTA and trust signal changes</li>
                    <li>Long-running strategic campaigns</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Your Assets */}
        {assets.length > 0 && (
          <div className="bg-surface border border-border rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Your Assets</h3>
            <p className="text-muted-foreground mb-4">
              Use these asset IDs when creating API campaigns:
            </p>
            <div className="space-y-2">
              {assets.slice(0, 5).map((asset) => (
                <div key={asset.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <div className="flex items-center gap-3">
                    <code className="font-mono text-sm text-primary">{asset.id}</code>
                    {asset.asset_slug && (
                      <code className="font-mono text-xs text-muted-foreground">{asset.asset_slug}</code>
                    )}
                    <span className="text-sm text-muted-foreground">- {asset.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      asset.status === 'ready' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 
                      'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                    }`}>
                      {asset.status}
                    </span>
                    <button 
                      onClick={() => handleCopy(asset.id, asset.id)}
                      className="p-1 rounded hover:bg-muted transition-colors"
                    >
                      {copied === asset.id ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-muted-foreground" />}
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {assets.length > 5 && (
              <Button 
                variant="ghost" 
                className="mt-4 w-full"
                onClick={() => navigate('/assets')}
              >
                View all {assets.length} assets <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        )}

        {/* Rate Limits */}
        <div className="bg-surface border border-border rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Rate Limits</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-muted/30 rounded-xl">
              <p className="text-sm text-muted-foreground mb-1">Requests per minute</p>
              <p className="text-2xl font-bold text-foreground">60</p>
            </div>
            <div className="p-4 bg-muted/30 rounded-xl">
              <p className="text-sm text-muted-foreground mb-1">Campaigns per day</p>
              <p className="text-2xl font-bold text-foreground">1,000</p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mt-4">
            Need higher limits? Contact us at support@pravik.ai
          </p>
        </div>
      </main>
    </div>
  );
}
